import React, { useEffect, useState } from 'react';

import ProcurementCategoriesTable from '../../procurement_categories/ProcurementCategoriesTable';
import ProcurementPoliciesCreateForm from '../../procurement_policies/ProcurementPoliciesCreateForm';
import ProcurementPoliciesTable from '../../procurement_policies/ProcurementPoliciesTable';

import GhostButton from './GhostButton';
import StepShell from './StepShell';

import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';

import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

/**
 * Procurement & contract setup. Embeds the existing procurement policy and
 * category tables; a new policy is created in a modal so the user never
 * leaves the wizard. Approval chains live on the Approvals page.
 */
export default function ProcStep({ step, urls, deferring, saving, onDefer, onNext, onRefresh }) {
    const [planUrl, setPlanUrl] = useState(null);
    // Remounting the table is how it refetches after an inline create.
    const [policiesVersion, setPoliciesVersion] = useState(0);
    // Each table opens from its own button and starts collapsed, as the
    // wireframe's summary layout does.
    const [open, setOpen] = useState({ policies: false, categories: false });
    const toggle = (section) => setOpen((current) => ({ ...current, [section]: !current[section] }));
    const createPolicyModal = useModal();
    const toast = useToast();

    useEffect(() => {
        axios
            .get(urls.data)
            .then(({ data }) => setPlanUrl(data.procurement_plan_url))
            .catch(() => toast.error($t('Something went wrong. Please try again.')));
    }, [urls.data]);

    // A first policy or category completes the step, so the rail is re-read.
    const refreshRail = () => {
        if (onRefresh) {
            onRefresh();
        }
    };

    const onPolicyCreated = () => {
        createPolicyModal.hide();
        setPoliciesVersion((version) => version + 1);
        refreshRail();
    };

    return (
        <StepShell
            step={step}
            lede={$t(
                'Link procurement to the budget so procurement plans, contracts and commitments flow from the lines they deliver. Required before the first procurement is raised, but not before budgeting can start.',
            )}
            deferring={deferring}
            saving={saving}
            onDefer={onDefer}
            onNext={onNext}
        >
            <div className="tw-flex tw-flex-col tw-gap-y-6">
                <div className="tw-flex tw-flex-wrap tw-gap-2.5">
                    <GhostButton expanded={open.policies} aria-controls="setup-wizard-proc-policies" onClick={() => toggle('policies')}>
                        {$t('+ Add your procurement policy')}
                    </GhostButton>
                    <GhostButton expanded={open.categories} aria-controls="setup-wizard-proc-categories" onClick={() => toggle('categories')}>
                        {$t('+ Add procurement categories')}
                    </GhostButton>
                    {planUrl && (
                        <GhostButton as="a" href={planUrl}>
                            {$t('+ Create procurement plans from budget lines')}
                        </GhostButton>
                    )}
                </div>

                {open.policies && (
                    <div id="setup-wizard-proc-policies">
                        <ProcurementPoliciesTable
                            key={policiesVersion}
                            indexUrl={urls.policies_index}
                            updateUrl={urls.policies_update}
                            deleteUrl={urls.policies_destroy}
                            onCreateInline={() => createPolicyModal.show()}
                            onChanged={refreshRail}
                        />
                    </div>
                )}

                {open.categories && (
                    <div id="setup-wizard-proc-categories">
                        <ProcurementCategoriesTable
                            indexUrl={urls.categories_index}
                            updateUrl={urls.categories_update}
                            createUrl={urls.categories_store}
                            deleteUrl={urls.categories_destroy}
                            onChanged={refreshRail}
                        />
                    </div>
                )}
            </div>

            <Modal id="setup-wizard-create-policy" modal={createPolicyModal}>
                <ModalHeader title={$t('Add New Procurement Policy Step')} close={() => createPolicyModal.hide()} />
                <div className="modal__form-container">
                    <ProcurementPoliciesCreateForm key={policiesVersion} storeUrl={urls.policies_store} onSuccess={onPolicyCreated} fullWidth />
                </div>
            </Modal>
        </StepShell>
    );
}
